import { useEffect, useRef, useState } from "react";
import { Play, Pause, SkipForward, Volume2, VolumeX } from "lucide-react";
import { PageHeader, Tabs } from "@/components/kit";
import { MEDITATIONS, CATEGORIES } from "@/data/meditation";
import { AmbientPicker } from "@/components/meditation/AmbientPicker";

const hasTTS = typeof window !== "undefined" && "speechSynthesis" in window;

const speak = (text, onEnd) => {
  const synth = window.speechSynthesis;
  synth.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.rate = 0.85;
  u.pitch = 0.95;
  u.onend = onEnd;
  u.onerror = (e) => { if (e.error !== "interrupted" && e.error !== "canceled") onEnd(); };
  window.__mmUtterance = u; // keep a reference so Chrome doesn't GC it before onend
  synth.resume();
  synth.speak(u);
};

const readMs = (text) => 2500 + text.split(/\s+/).length * 380;

const Player = ({ m, onClose }) => {
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [resting, setResting] = useState(false);
  const [voice, setVoice] = useState(hasTTS);
  const pauseMs = Math.min(6000, Math.max(3000, (m.durationMinutes * 60000) / m.transcript.length - 8000));
  const t = useRef(null);

  useEffect(() => {
    if (!playing) return;
    let live = true;
    const advance = () => {
      if (!live) return;
      if (i < m.transcript.length - 1) setI(i + 1);
      else setPlaying(false);
    };
    let rested = false;
    let keepAlive;
    const rest = () => {
      if (!live || rested) return;
      rested = true;
      clearTimeout(t.current);
      clearInterval(keepAlive);
      setResting(true);
      t.current = setTimeout(advance, pauseMs);
    };
    setResting(false);
    if (voice) {
      speak(m.transcript[i], rest);
      // Mobile browsers sometimes stall speech or never fire onend: nudge it and move on after the expected time
      keepAlive = setInterval(() => window.speechSynthesis.resume(), 4000);
      t.current = setTimeout(rest, readMs(m.transcript[i]) * 1.3);
    } else {
      t.current = setTimeout(rest, readMs(m.transcript[i]));
    }
    return () => { live = false; clearTimeout(t.current); clearInterval(keepAlive); if (hasTTS) window.speechSynthesis.cancel(); };
  }, [playing, i, voice, m, pauseMs]);

  useEffect(() => () => window.speechSynthesis?.cancel(), []);
  const done = i === m.transcript.length - 1 && !playing;

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center p-6 text-white" style={{ background: "#0f0c18" }} data-testid="meditation-player">
      <button onClick={onClose} className="absolute top-6 right-6 text-white/70 hover:text-white text-sm font-semibold" data-testid="meditation-close-button">Close</button>
      <div className="text-6xl mb-4" style={{ filter: `drop-shadow(0 0 30px ${m.color})` }}>{m.icon}</div>
      <h2 className="text-2xl font-bold">{m.title}</h2>
      <p className="text-white/50 text-sm">{m.duration}</p>
      <p key={i} className="font-display text-2xl sm:text-3xl text-center max-w-2xl mt-12 min-h-[120px] leading-snug mm-fade-up" data-testid="meditation-line">{m.transcript[i]}</p>
      <div className={`w-3 h-3 rounded-full mt-6 transition-opacity ${playing ? "opacity-100 animate-ping" : "opacity-0"}`} style={{ background: m.color }} data-testid="meditation-breathing-dot" />
      <p className="text-white/40 text-xs mt-3 h-4" data-testid="meditation-status">{playing ? (resting ? "Breathe…" : voice ? "Listening…" : "") : ""} {playing && `${i + 1} / ${m.transcript.length}`}</p>
      <div className="w-full max-w-md h-1 bg-white/10 rounded-full mt-6"><div className="h-full rounded-full transition-all duration-700" style={{ width: `${((i + 1) / m.transcript.length) * 100}%`, background: m.color }} /></div>
      <div className="flex gap-3 mt-8">
        <button className="mm-btn" style={{ background: m.color }} onClick={() => {
          if (voice && !playing) window.speechSynthesis.speak(new SpeechSynthesisUtterance(" ")); // unlock speech on iOS within the tap
          if (done) setI(0);
          setPlaying(done || !playing);
        }} data-testid="meditation-play-button">
          {playing ? <Pause size={18} /> : <Play size={18} />}{playing ? "Pause" : done ? "Restart" : "Begin"}
        </button>
        <button className="mm-btn-ghost" onClick={() => setI(Math.min(i + 1, m.transcript.length - 1))} data-testid="meditation-next-button"><SkipForward size={16} /></button>
        <button className="mm-btn-ghost" onClick={() => setVoice(hasTTS && !voice)} data-testid="meditation-voice-toggle">{voice ? <Volume2 size={16} /> : <VolumeX size={16} />}</button>
      </div>
      <AmbientPicker />
    </div>
  );
};

export default function Meditation() {
  const [cat, setCat] = useState("all");
  const [active, setActive] = useState(null);
  const list = MEDITATIONS.filter((m) => cat === "all" || m.category === cat);
  return (
    <div data-testid="meditation-page">
      <PageHeader back title="Meditations" subtitle="Guided sessions with calm voice narration" />
      <Tabs items={CATEGORIES} value={cat} onChange={setCat} testPrefix="meditation-cat" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mm-stagger">
        {list.map((m) => (
          <button key={m.id} onClick={() => setActive(m)} className="mm-card mm-card-hover p-5 text-left" data-testid={`meditation-${m.id}`}>
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl" style={{ background: `${m.color}22` }}>{m.icon}</div>
            <div className="font-semibold mt-3">{m.title}</div>
            <div className="text-xs text-mm-muted">{m.duration} · {m.category}</div>
            <p className="text-sm text-mm-muted mt-2">{m.description}</p>
          </button>
        ))}
      </div>
      {active && <Player m={active} onClose={() => setActive(null)} />}
    </div>
  );
}
